import axios from "axios";
import moment from "moment";

const base = process.env.REACT_APP_API;

const request = () => (url, token?) => {
  const rq = axios({
    url,
    method: "get",
    headers: {
      Authorization: token
    }
  });

  axios.interceptors.response.use(null, (error) => {
    if (error.config && error.response && error.response.status === 504) {
      return rq;
    }

    return Promise.reject(error);
  });

  return rq;
};

const r = request();

export const onCreateReport = async (config) => {
  const request = await axios.post(`${base}/reports/create`, {
    period: config.period,
    host_level: config.host_level,
    host_id: config.host_id,
    start_date: config.dates[0],
    end_date: config.dates[1]
  });

  return request;
};

const configDate = (dates) => {
  const startDate = moment(dates[0]).format().split("T")[0];
  const endDate = moment(dates[1]).format().split("T")[0];

  return startDate === endDate ? startDate : `${startDate}/${endDate}`;
};

const getPreviousPeriod = (date) => {
  const d = moment(date);

  return d.subtract(1, "year").format("YYYY-MM-DD");
};

const getMainMetricCounts = (config) => {
  const { host_level, host_id, dates, period, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/${period}/${configDate(
      dates
    )}?group_by=user_type&agg=uniques__sum&rank_hosts=prov_siblings&rank_extract_target_host=True`,
    token
  );
};

const getMainMetricPie = (config) => {
  const { host_level, host_id, dates, period, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/${period}/${configDate(
      dates
    )}?filter__user_type=visitor&group_by=user_origin&agg=uniques__sum&rank_hosts=prov_siblings&rank_extract_target_host=True`,
    token
  );
};

const getTopDay = (config) => {
  const { host_level, host_id, dates, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/daily/${configDate(
      dates
    )}?filter__user_type=visitor&group_by=user_type,date&agg=uniques__sum&order_by=-uniques__sum&top_k=1`,
    token
  );
};

const getTopHour = (config) => {
  const { host_level, host_id, dates, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/hourly/${configDate(
      dates
    )}?filter__user_type=visitor&group_by=user_type,date,date__hour&agg=uniques__sum&order_by=-uniques__sum&top_k=1`,
    token
  );
};

export const getMainMetrics = async (config) => {
  const [counts, pie, topDay, topHour] = await Promise.all([
    getMainMetricCounts(config),
    getMainMetricPie(config),
    getTopDay(config),
    getTopHour(config)
  ]);
  const previousYear = config.dates.map((d) => getPreviousPeriod(d));

  const [
    previousTopDay,
    previousCounts,
    previousPie,
    previousTopHour
  ] = await Promise.all([
    getTopDay({ ...config, dates: previousYear }),
    getMainMetricCounts({ ...config, dates: previousYear }),
    getMainMetricPie({ ...config, dates: previousYear }),
    getTopHour({ ...config, dates: previousYear })
  ]);

  return {
    current: {
      counts: counts.data.data,
      pie: pie.data.data,
      topDay: topDay.data.data,
      topHour: topHour.data.data
    },
    previous: {
      counts: previousCounts.data.data,
      pie: previousPie.data.data,
      topDay: previousTopDay.data.data,
      topHour: previousTopHour.data.data
    }
  };
};

const getUniqueVisitorsPerProvince = (config, overnight) => {
  const { host_level, host_id, dates, period, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/${period}/${configDate(
      dates
    )}?filter__user_type=visitor&filter__user_origin=national&plot__value_type=percentage&group_by=residence_province&agg=uniques__sum&response_type=pie&plot__label_field=residence_province&plot__value_field=uniques__sum&plot__max_records=7&order_by=-uniques__sum${
      overnight ? "&filter__visit_type=overnight" : ""
    }`,
    token
  );
};

const getUniqueVisitorsPerCountry = (config, overnight) => {
  const { host_level, host_id, dates, period, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/${period}/${configDate(
      dates
    )}?filter__user_type=visitor&filter__user_origin=international&plot__value_type=percentage&group_by=country_iso&agg=uniques__sum&response_type=pie&plot__label_field=country_iso&plot__value_field=uniques__sum&plot__max_records=7&order_by=-uniques__sum${
      overnight ? "&filter__visit_type=overnight" : ""
    }`,
    token
  );
};

const getAllUniqueVisitorsByLength = (config, overnight) => {
  const { host_level, host_id, dates, period, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/${period}/${configDate(
      dates
    )}?filter__user_type=visitor&plot__value_type=percentage&group_by=user_origin${
      overnight ? ",overnight_type" : ",daytime_type"
    }&agg=uniques__sum${overnight ? "&filter__visit_type=overnight" : ""}`,
    token
  );
};

const getUniqueVisitorsHistograms = (config, overnight) => {
  const { host_level, host_id, dates, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/daily/${configDate(
      dates
    )}?filter__user_type=visitor&group_by=user_origin,date&agg=uniques__sum&order_by=date${
      overnight ? "&filter__visit_type=overnight" : ""
    }`,
    token
  );
};

const duration_bin_hour_averages = {
  "<2": 1,
  "2-5": 3.5,
  "6-12": 9,
  ">12": 18,
  overnight: 99 // only used to sort
};

const overnight_night_averages = {
  "0-1": 1,
  "2": 2,
  "3-6": 4.5,
  "7-13": 10,
  ">13": 21.5,
  weekender: 99
};

const sortDurationBins = (bins_averages) => {
  return function (a, b) {
    if (bins_averages[a.duration_bin] > bins_averages[b.duration_bin]) {
      return 1;
    } else if (bins_averages[a.duration_bin] < bins_averages[b.duration_bin]) {
      return -1;
    } else {
      return 0;
    }
  };
};

const groupBy = (data, user_origin, field, null_field_value) => {
  return data
    .filter((d) => d.user_origin === user_origin)
    .map((d) => ({
      duration_bin: d[field] != null ? d[field] : null_field_value,
      uniques: d.uniques__sum
    }));
};

const getUniqueVisitorsPerHour = (config) => {
  const { host_level, host_id, dates, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/hourly/${configDate(
      dates
    )}?filter__user_type=visitor&group_by=user_origin,date__trunc__day,date__hour&agg=uniques__sum`,
    token
  );
};

export const getUniqueVisitors = async (config) => {
  const previousYear = config.dates.map((d) => getPreviousPeriod(d));

  const [
    country,
    province,
    histoNationalsInternationals,
    byLengthAll,
    previousByLengthAll
  ] = await Promise.all([
    getUniqueVisitorsPerCountry(config, false),
    getUniqueVisitorsPerProvince(config, false),
    getUniqueVisitorsHistograms(config, false),
    getAllUniqueVisitorsByLength(config, false),
    getAllUniqueVisitorsByLength({ ...config, dates: previousYear }, false)
  ]);
  const perHour = await getUniqueVisitorsPerHour(config); // this is an expensive call

  let histoNationals,
    histoInternationals = { data: [] };
  let nationalLengthAllData = [];
  let internationalLengthAllData = [];
  let previousNationalLengthAllData = [];
  let previousInternationalLengthAllData = [];
  let avgStayDataAll = { national: null, international: null };

  if (histoNationalsInternationals.data) {
    histoNationals = histoNationalsInternationals.data.data.filter(
      (d) => d.user_origin === "national"
    );
    histoInternationals = histoNationalsInternationals.data.data.filter(
      (d) => d.user_origin === "international"
    );
  }

  if (byLengthAll.data) {
    nationalLengthAllData = groupBy(
      byLengthAll.data.data,
      "national",
      "daytime_type",
      "overnight"
    ).sort(sortDurationBins(duration_bin_hour_averages));
    internationalLengthAllData = groupBy(
      byLengthAll.data.data,
      "international",
      "daytime_type",
      "overnight"
    ).sort(sortDurationBins(duration_bin_hour_averages));
    avgStayDataAll.national = calcAverageStayHours(
      nationalLengthAllData,
      duration_bin_hour_averages
    );
    avgStayDataAll.international = calcAverageStayHours(
      internationalLengthAllData,
      duration_bin_hour_averages
    );
  }

  if (previousByLengthAll.data) {
    previousNationalLengthAllData = groupBy(
      previousByLengthAll.data.data,
      "national",
      "daytime_type",
      "overnight"
    ).sort(sortDurationBins(duration_bin_hour_averages));
    previousInternationalLengthAllData = groupBy(
      previousByLengthAll.data.data,
      "international",
      "daytime_type",
      "overnight"
    ).sort(sortDurationBins(duration_bin_hour_averages));
  }

  return {
    country: country.data,
    province: province.data,
    histoNationals: histoNationals,
    histoInternationals: histoInternationals,
    byLengthAll: {
      nationalLengthAll: nationalLengthAllData,
      internationalLengthAll: internationalLengthAllData,
      avgStayAll: avgStayDataAll
    },
    previousByLengthAll: {
      previousNationalLengthAll: previousNationalLengthAllData,
      previousInternationalLengthAll: previousInternationalLengthAllData
    },
    perHour: perHour.data.data
      ? getSeries(perHour.data.data, config.period)
      : { national: {}, international: {} }
  };
};

export const getUniqueVisitorsOvernight = async (config) => {
  const [
    country,
    province,
    histoNationalsInternationals,
    byLengthAll
  ] = await Promise.all([
    getUniqueVisitorsPerCountry(config, true),
    getUniqueVisitorsPerProvince(config, true),
    getUniqueVisitorsHistograms(config, true),
    getAllUniqueVisitorsByLength(config, true)
  ]);

  let histoNationals,
    histoInternationals = { data: [] };
  let nationalLengthAllData = [];
  let internationalLengthAllData = [];
  let avgStayDataAll = { national: null, international: null };

  if (histoNationalsInternationals.data) {
    histoNationals = histoNationalsInternationals.data.data.filter(
      (d) => d.user_origin === "national"
    );
    histoInternationals = histoNationalsInternationals.data.data.filter(
      (d) => d.user_origin === "international"
    );
  }

  if (byLengthAll.data) {
    nationalLengthAllData = groupBy(
      byLengthAll.data.data,
      "national",
      "overnight_type",
      "default"
    ).sort(sortDurationBins(overnight_night_averages));
    internationalLengthAllData = groupBy(
      byLengthAll.data.data,
      "international",
      "overnight_type",
      "default"
    ).sort(sortDurationBins(overnight_night_averages));
    avgStayDataAll.national = calcAverageStayHours(
      nationalLengthAllData,
      overnight_night_averages
    );
    avgStayDataAll.international = calcAverageStayHours(
      internationalLengthAllData,
      overnight_night_averages
    );
  }

  return {
    country: country.data,
    province: province.data,
    histoNationals: histoNationals,
    histoInternationals: histoInternationals,
    byLengthAll: {
      nationalLengthAll: nationalLengthAllData,
      internationalLengthAll: internationalLengthAllData,
      avgStayAll: avgStayDataAll
    }
  };
};

const calcAverageStayHours = (data, bins_averages) => {
  let reduced_data = data
    .filter((x) => {
      return x.duration_bin !== "overnight" && x.duration_bin !== "weekender";
    })
    .reduce(
      (a, b) => {
        return {
          sum: a.sum + bins_averages[b.duration_bin] * b.uniques,
          count: a.count + b.uniques
        };
      },
      { sum: 0, count: 0 }
    );
  return (reduced_data.sum / reduced_data.count).toFixed(2);
};

const getSeries = (data, period) => {
  const series = {
    national: {},
    international: {}
  };

  const seriesStats = { min: 99999, max: -1, total: 0 };

  for (const hour in Array.from({ length: 24 }, (x, i) => i)) {
    series.national[hour] = { ...seriesStats };
    series.international[hour] = { ...seriesStats };
  }

  const dates = {};

  for (let index = 0; index < data.length; index++) {
    const { date__hour, uniques__sum, user_origin, date__trunc__day } = data[
      index
    ];
    const element = series[user_origin][date__hour];

    element.max = element.max > uniques__sum ? element.max : uniques__sum;
    element.min =
      element.min !== 0 && element.min < uniques__sum
        ? element.min
        : uniques__sum;
    element.total = element.total + uniques__sum;

    if (!dates.hasOwnProperty(date__trunc__day)) {
      dates[date__trunc__day] = true;
    }
  }

  const days = Object.keys(dates).length;

  // compute averages
  series.national = Object.keys(series.national).map((d) => ({
    ...series.national[d],
    total: Math.floor(series.national[d].total / days)
  }));

  series.international = Object.keys(series.international).map((d) => ({
    ...series.international[d],
    total: Math.floor(series.international[d].total / days)
  }));

  return series;
};

const getTopProvinces = (config) => {
  const { host_level, host_id, dates, period, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/${period}/${configDate(
      dates
    )}?filter__user_type=visitor&agg=uniques__sum&rank_hosts=siblings&rank_top_k=5&rank_extract_host_labels=True`,
    token
  );
};

const getTopMun = (config) => {
  const { host_level, host_id, dates, period, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/${period}/${configDate(
      dates
    )}?filter__user_type=visitor&agg=uniques__sum&rank_hosts=grandchildren&rank_top_k=5&rank_extract_host_labels=True`,
    token
  );
};

const getTopDis = (config) => {
  const { host_level, host_id, dates, period, token } = config;

  return r(
    `${base}/tourism/uniques/${host_level}/${host_id}/${period}/${configDate(
      dates
    )}?filter__user_type=visitor&agg=uniques__sum&rank_hosts=childs&rank_top_k=5`,
    token
  );
};

export const getRanks = async (config) => {
  const [topProvinces, topMun] = await Promise.all([
    getTopProvinces({
      ...config,
      host_level: "PRO",
      host_id: config.hosts.PRO.id
    }),
    getTopMun({ ...config, host_level: "PRO", host_id: config.hosts.PRO.id })
  ]);

  if (config.host_level === "MUN") {
    const topDis = await getTopDis(config);

    return {
      topProvinces: topProvinces.data,
      topMun: topMun.data,
      topDis: topDis.data
    };
  } else {
    return {
      topProvinces: topProvinces.data,
      topMun: topMun.data,
      topDis: []
    };
  }
};

export const getHostLevels = async (level, filter?) => {
  const fil = filter ? "?filter__host_parent_id=" + filter : "";
  return r(`${base}/meta/grid/hosts/${level}${fil}`);
};

export const makePayment = async (config) => {
  const request = await axios.post(process.env.REACT_APP_ST_API, {
    body: config
  });
  //const request = await axios.post("http://localhost:8888/payment", { body: config });

  return request;
};

export const finalizeReport = async (token) => {
  const request = await axios.post(
    `${base}/reports/finalize`,
    {},
    {
      headers: { Authorization: token }
    }
  );

  return request;
};
